import React, { useState, useEffect } from 'react';
import { Plus, Save, Trash2, Edit2, Loader2 } from 'lucide-react';
import { Notification } from '../components/Notification';
import { ConfirmDialog } from '../components/ConfirmDialog';
import type { Product, ProductOption, ProductOptionValue } from '../types';
import { productService } from '../data/productService';

export function Admin() {
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [newProduct, setNewProduct] = useState<Partial<Product>>({
    name: '',
    price: 0,
    image: '',
    description: '',
    options: []
  });
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [newOption, setNewOption] = useState({ 
    name: '', 
    values: '',
    costs: ''
  });
  const [saving, setSaving] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<{ show: boolean; id: number | null }>({
    show: false,
    id: null
  });

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const fetchedProducts = await productService.getProducts();
      setProducts(fetchedProducts);
    } catch (err) {
      setError('Failed to load products. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    const target = editingProduct ? setEditingProduct : setNewProduct;
    
    target(prev => ({
      ...prev,
      [name]: name === 'price' ? parseFloat(value) || 0 : value
    }));
  };

  const handleOptionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setNewOption(prev => ({ ...prev, [name]: value }));
  };

  const addOption = () => {
    if (newOption.name && newOption.values) {
      const values = newOption.values.split(',').map(v => v.trim());
      const costs = newOption.costs.split(',').map(c => parseFloat(c.trim()) || 0);
      
      const option: ProductOption = {
        name: newOption.name,
        values: values.map((value, index) => ({
          value,
          additionalCost: costs[index] || 0
        }))
      };

      const target = editingProduct ? setEditingProduct : setNewProduct;
      target(prev => ({
        ...prev,
        options: [...(prev.options || []), option]
      }));
      setNewOption({ name: '', values: '', costs: '' });
    }
  };

  const removeOption = (index: number) => {
    const target = editingProduct ? setEditingProduct : setNewProduct;
    target(prev => ({
      ...prev,
      options: prev.options?.filter((_, i) => i !== index) || []
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      if (editingProduct) {
        await productService.updateProduct(editingProduct as Product);
        setSuccessMessage('Product updated successfully!');
      } else {
        await productService.addProduct(newProduct as Product);
        setSuccessMessage('Product added successfully!');
      }
      await fetchProducts();
      setEditingProduct(null);
      setNewProduct({
        name: '',
        price: 0,
        image: '',
        description: '',
        options: []
      });
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      setError('Failed to save product. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const startEditing = (product: Product) => {
    setError(null);
    setEditingProduct(product);
    setNewProduct({
      name: '',
      price: 0,
      image: '',
      description: '',
      options: []
    });
  };

  const cancelEditing = () => {
    setEditingProduct(null);
    setNewProduct({
      name: '',
      price: 0,
      image: '',
      description: '',
      options: []
    });
  };

  const confirmDelete = (id: number) => {
    setDeleteConfirm({ show: true, id });
  };

  const handleDelete = async () => {
    if (!deleteConfirm.id) return;
    
    try {
      await productService.deleteProduct(deleteConfirm.id);
      setSuccessMessage('Product deleted successfully!');
      await fetchProducts();
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      setError('Failed to delete product. Please try again.');
    } finally {
      setDeleteConfirm({ show: false, id: null });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#6389af]" />
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      {deleteConfirm.show && (
        <ConfirmDialog
          title="Delete Product"
          message="Are you sure you want to delete this product? This action cannot be undone."
          onConfirm={handleDelete}
          onCancel={() => setDeleteConfirm({ show: false, id: null })}
        />
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-2xl font-semibold mb-6">
            {editingProduct ? 'Edit Product' : 'Add New Product'}
          </h2>

          {successMessage && (
            <div className="mb-6">
              <Notification
                type="success"
                message={successMessage}
                onClose={() => setSuccessMessage(null)}
              />
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Product Name
                </label>
                <input
                  type="text"
                  name="name"
                  value={(editingProduct || newProduct).name || ''}
                  onChange={handleInputChange}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#6389af] focus:border-[#6389af]"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Base Price (£)
                </label>
                <input
                  type="number"
                  name="price"
                  value={(editingProduct || newProduct).price || ''}
                  onChange={handleInputChange}
                  step="0.01"
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#6389af] focus:border-[#6389af]"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Image URL (optional)
                </label>
                <input
                  type="url"
                  name="image"
                  value={(editingProduct || newProduct).image || ''}
                  onChange={handleInputChange}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#6389af] focus:border-[#6389af]"
                  placeholder="Leave empty to use default image"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  name="description"
                  value={(editingProduct || newProduct).description || ''}
                  onChange={handleInputChange}
                  rows={3}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#6389af] focus:border-[#6389af]"
                />
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="text-lg font-medium">Product Options</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <input
                    type="text"
                    name="name"
                    value={newOption.name}
                    onChange={handleOptionChange}
                    placeholder="Option name (e.g., Size)"
                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#6389af] focus:border-[#6389af]"
                  />
                </div>
                <div>
                  <input
                    type="text"
                    name="values"
                    value={newOption.values}
                    onChange={handleOptionChange}
                    placeholder="Values (comma-separated)"
                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#6389af] focus:border-[#6389af]"
                  />
                </div>
                <div className="relative">
                  <input
                    type="text"
                    name="costs"
                    value={newOption.costs}
                    onChange={handleOptionChange}
                    placeholder="Additional costs (comma-separated)"
                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#6389af] focus:border-[#6389af] pr-16"
                  />
                  <button
                    type="button"
                    onClick={addOption}
                    className="absolute right-0 top-0 bottom-0 px-4 bg-[#6389af] text-white rounded-r-md hover:bg-[#557694]"
                  >
                    <Plus size={20} />
                  </button>
                </div>
              </div>

              <div className="text-sm text-gray-500 mt-1">
                Example: Name: "Size", Values: "Small,Medium,Large", Costs: "0,1.50,3.00"
              </div>

              {(editingProduct || newProduct).options?.map((option, index) => (
                <div key={index} className="flex items-center justify-between bg-gray-50 p-3 rounded-md">
                  <div>
                    <span className="font-medium">{option.name}:</span>{' '}
                    {option.values.map((v, i) => (
                      <span key={i} className="mr-2">
                        {v.value}
                        {v.additionalCost > 0 && ` (+£${v.additionalCost.toFixed(2)})`}
                        {i < option.values.length - 1 ? ',' : ''}
                      </span>
                    ))}
                  </div>
                  <button
                    type="button"
                    onClick={() => removeOption(index)}
                    className="text-red-500 hover:text-red-700"
                  >
                    <Trash2 size={20} />
                  </button>
                </div>
              ))}
            </div>

            {error && (
              <Notification
                type="error"
                message={error}
                onClose={() => setError(null)}
              />
            )}

            <div className="flex gap-4">
              <button
                type="submit"
                disabled={saving}
                className="flex-1 flex items-center justify-center gap-2 bg-[#6389af] text-white py-3 rounded-md hover:bg-[#557694] disabled:bg-[#6389af]/50"
              >
                {saving && <Loader2 size={20} className="animate-spin" />}
                <Save size={20} />
                {saving ? 'Saving...' : 'Save Product'}
              </button>
              {editingProduct && (
                <button
                  type="button"
                  onClick={cancelEditing}
                  className="flex-1 py-3 border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Cancel
                </button>
              )}
            </div>
          </form>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-2xl font-semibold mb-6">Current Products</h2>
          <div className="space-y-4">
            {products.map(product => (
              <div key={product.id} className="flex items-center justify-between border-b pb-4">
                <div className="flex items-center gap-4">
                  <img
                    src={product.image}
                    alt={product.name}
                    className="w-16 h-16 object-cover rounded"
                  />
                  <div>
                    <h3 className="font-medium">{product.name}</h3>
                    <p className="text-gray-600">Base Price: £{product.price.toFixed(2)}</p>
                    {product.options.length > 0 && (
                      <div className="text-sm text-gray-500 mt-1">
                        {product.options.map((option, index) => (
                          <div key={index}>
                            {option.name}: {option.values.map((v, i) => (
                              <span key={i}>
                                {v.value}
                                {v.additionalCost > 0 && ` (+£${v.additionalCost.toFixed(2)})`}
                                {i < option.values.length - 1 ? ', ' : ''}
                              </span>
                            ))}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => startEditing(product)}
                    className="text-[#6389af] hover:text-[#557694]"
                    title="Edit product"
                  >
                    <Edit2 size={20} />
                  </button>
                  <button
                    onClick={() => confirmDelete(product.id)}
                    className="text-red-500 hover:text-red-700"
                    title="Delete product"
                  >
                    <Trash2 size={20} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}